import type { Channel } from '@/Enums/Channel.enum';
import type { IBidCpmGuidanceRange } from '@/Interfaces/IBidCpmGuidanceRange';
import type { IProduct } from '@/Interfaces/Product.interface';
import { RON_PRODUCT, getProductControlName } from '@/constants/forecaster';
import { calculateBidCpmGuidance } from '@/helpers/bidCpmGuidance';
import { getCurrentChannelProducts } from '@/helpers/forecaster';
import useProductDomains from '@/hooks/useProductDomains';
import { useAppSelector } from '@/store/hooks';
import { selectShowBidCpmGuidanceInForecaster } from '@/store/selectors/currentWhitelabel.selectors';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

/**
 * Scopes guidance to the inventory the forecast itself queries (`BeeswaxForecast.pubTargeting`):
 * the publisher whitelist only applies with Custom List on, and domains carry the CPMs that the
 * publishers of domain-backed products lack.
 */
const useForecasterBidCpmGuidance = (currentChannel?: Channel): IBidCpmGuidanceRange | null => {
  const showGuidance = useAppSelector(selectShowBidCpmGuidanceInForecaster);
  const products = useAppSelector(state => state.forecaster.products);
  const { control } = useFormContext();

  const selectedProductId = useWatch({ control, name: getProductControlName(currentChannel) });
  const isCustomList = useWatch({ control, name: `${currentChannel}.isCustomList` });
  const checkedPublishers = useWatch({
    control,
    name: `${getProductControlName(currentChannel)}_${selectedProductId}_pubs`,
  });

  const selectedProduct = useMemo(() => {
    if (!showGuidance) return null;
    if (!selectedProductId || selectedProductId === RON_PRODUCT.id) return null;
    const channelProducts: IProduct[] = getCurrentChannelProducts(products, currentChannel);
    return channelProducts.find(product => product.id === +selectedProductId) ?? null;
  }, [showGuidance, currentChannel, selectedProductId, products]);

  const scopedPublishers = useMemo(() => {
    const publishers = selectedProduct?.publishers ?? [];
    if (!isCustomList) return publishers;
    return publishers.filter(publisher => !!checkedPublishers?.[publisher.id]);
  }, [selectedProduct, isCustomList, checkedPublishers]);

  const publisherGuidance = useMemo(
    () =>
      selectedProduct ? calculateBidCpmGuidance(scopedPublishers.map(({ bidCpmGuidance }) => bidCpmGuidance)) : null,
    [selectedProduct, scopedPublishers],
  );

  // An empty Custom List forecasts zero impressions, so there is no inventory left to advise on.
  const isEmptyCustomList = !!isCustomList && !scopedPublishers.length;
  const needsDomains = !publisherGuidance && !isEmptyCustomList && !!selectedProduct?.hasDomains;
  const { domains } = useProductDomains(needsDomains ? selectedProduct?.id : null);

  return useMemo(() => {
    if (publisherGuidance) return publisherGuidance;
    if (!needsDomains) return null;
    return calculateBidCpmGuidance(domains.map(({ bidCpmGuidance }) => bidCpmGuidance));
  }, [publisherGuidance, needsDomains, domains]);
};

export default useForecasterBidCpmGuidance;
